import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Button } from '@telus-uds/components-web';

import { Error } from '../design/elements';
import FormField from './formField';
import {
  GroupTitle,
  GroupWrapper,
  LocationWrapper,
  LocationGroupTitle,
  LocationButton,
  InlineRepeatedFormField
} from './css';
import {
  keyServiceAddresses,
  hasQuebecServiceAddress,
  fieldEmergencyPremiseContactNumber,
  fieldEmergencyExtension,
  fieldpreferredLanguageOfContact,
  fieldpreferredLanguageOfContactOne,
  fieldpreferredLanguageOfContactTwo,
  fieldEmergencyPremiseContactNumberNonRepeated,
  fieldEmergencyExtensionNonRepeated,
  winbackPrefferedInstallationNoteProvinces,
  statementOfWork,
  appendices
} from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import stringInterpolation from '../utils/stringInterpolation';
import request from '../utils/request';
import { indexToLetter } from '../utils/utils';

const RepeatedForm = ({
  data,
  formErrors = {},
  formFields,
  orderData,
  setFormFields,
  isEditing,
  isFormTouched,
  setIsFormTouched
}) => {
  const {
    name,
    key,
    repeatBasedOnBackendField,
    formFields: contentFulFields,
    formTitle
  } = data;
  const localizedStrings = useLocalizedStringsContext();
  const {
    formButtons: { addNewFormButtonLabel, deleteFormButtonLabel },
    modalForm: { fieldRequiredMessage }
  } = localizedStrings;
  let formsList = formFields[key];
  formsList =
    formsList && (formsList.length ? formsList : Object.values(formsList));
  const repeatedFormError = formErrors[key];
  const formObjectKeys = useMemo(
    () =>
      contentFulFields.reduce((acc, { fields: { name: formName, value } }) => {
        acc[formName] = value;
        return acc;
      }, {}),
    [contentFulFields]
  );

  const isCustomerLocation = key === keyServiceAddresses;
  const isAddEmergencyContact = key === 'emergencyContactDetails';
  const isStatementOfWork = key === statementOfWork;
  const isAppendices = key === appendices;

  const formListItemNumberingFormat = {
    [statementOfWork]: 'numbers',
    [appendices]: 'letters'
  };

  const fieldsMap = useMemo(
    () =>
      contentFulFields.reduce((acc, { fields }) => {
        acc[fields.name] = { ...fields };
        return acc;
      }, {}),
    [contentFulFields]
  );

  const hasQuebecSerAddress = () => {
    return (
      orderData.serviceAddress.includes(hasQuebecServiceAddress[0]) ||
      orderData.serviceAddress.includes(hasQuebecServiceAddress[1]) ||
      orderData.serviceAddress.includes(hasQuebecServiceAddress[2])
    );
  };

  const getOriginalFieldSequence = () => {
    return get(data, 'formFields', []).map(
      formFieldItem => formFieldItem.fields.name
    );
  };

  const getFieldsToRemove = () => {
    if (!isAddEmergencyContact || hasQuebecSerAddress()) {
      return [];
    }

    const province = request.getProvince();
    const fieldsToRemove = [
      fieldpreferredLanguageOfContact,
      fieldpreferredLanguageOfContactOne,
      fieldpreferredLanguageOfContactTwo
    ];

    // Add province-specific fields to remove
    if (winbackPrefferedInstallationNoteProvinces.includes(province)) {
      fieldsToRemove.push(
        fieldEmergencyExtensionNonRepeated,
        fieldEmergencyPremiseContactNumberNonRepeated
      );
    }

    return fieldsToRemove;
  };

  const getFilteredFormsList = () => {
    if (!formsList) {
      return formsList;
    }

    const fieldsToRemove = getFieldsToRemove();
    if (fieldsToRemove.length === 0) {
      return formsList;
    }

    // Return filtered formsList with specified fields removed
    return formsList.map(form => {
      const filteredForm = { ...form };
      fieldsToRemove.forEach(fieldKey => {
        delete filteredForm[fieldKey];
      });
      return filteredForm;
    });
  };

  const getFilteredFieldsMap = () => {
    const fieldsToRemove = getFieldsToRemove();
    if (fieldsToRemove.length === 0) {
      return fieldsMap;
    }

    const filteredMap = { ...fieldsMap };
    fieldsToRemove.forEach(fieldKey => {
      delete filteredMap[fieldKey];
    });
    return filteredMap;
  };

  useEffect(() => {
    if (isEditing && orderData && orderData.id && orderData[key]) {
      const updatedFormFields = { ...formFields };
      updatedFormFields[key] = [...orderData[key]];
      setFormFields(updatedFormFields);
      if (isFormTouched) {
        setIsFormTouched(false);
      }
    }
  }, [isEditing, orderData]);

  const addNewForm = () => {
    if (
      orderData[repeatBasedOnBackendField] > formsList.length ||
      isCustomerLocation ||
      isAddEmergencyContact ||
      isStatementOfWork ||
      isAppendices
    ) {
      if (isAddEmergencyContact && formsList.length === 5) {
        return;
      }
      const updatedFormFields = { ...formFields };
      updatedFormFields[key] = [...formsList, { ...formObjectKeys }];
      setFormFields(updatedFormFields);
    }
  };

  const removeFormGroup = index => {
    const fieldList = formsList;
    fieldList.splice(index, 1);
    const updatedFormFields = { ...formFields };
    updatedFormFields[key] = formsList;
    setFormFields(updatedFormFields);
  };

  const handleFormField = (index, fieldKey, value) => {
    const fieldList = formsList;
    fieldList[index][fieldKey] = value;
    const updatedFormFields = { ...formFields };
    updatedFormFields[key] = fieldList;
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
    setFormFields(updatedFormFields);
  };

  const MainWrapper = isCustomerLocation ? LocationWrapper : GroupWrapper;
  const TitleWrapper = isCustomerLocation ? LocationGroupTitle : GroupTitle;

  const renderDeleteButton = index => {
    if (isCustomerLocation) {
      return (
        <LocationButton
          style={{ color: 'red', fontSize: '12px', marginLeft: '5em' }}
          onClick={() => {
            removeFormGroup(index);
          }}
        >
          {deleteFormButtonLabel}
        </LocationButton>
      );
    }

    return (
      <button
        type="button"
        style={{ float: 'right' }}
        className="frg-icon icon-minus"
        onClick={() => {
          removeFormGroup(index);
        }}
      />
    );
  };

  const renderAddButton = index => {
    if (isCustomerLocation) {
      return (
        <LocationButton onClick={() => addNewForm()}>
          » {stringInterpolation(addNewFormButtonLabel, formTitle)}
        </LocationButton>
      );
    }

    return (
      !isAddEmergencyContact && (
        <Button
          type="submit"
          variant={{ priority: 'high' }}
          disabled={index === orderData[repeatBasedOnBackendField] - 1}
          onClick={() => addNewForm()}
        >
          {stringInterpolation(addNewFormButtonLabel, formTitle)}
        </Button>
      )
    );
  };

  const getSerialNumber = index => {
    const numberingFormat = formListItemNumberingFormat[key];
    switch (numberingFormat) {
      case 'letters':
        return indexToLetter(index);
      case 'numbers':
        return index + 1;
      default:
        return '';
    }
  };

  const filteredFormsList = getFilteredFormsList();
  const filteredFieldsMap = getFilteredFieldsMap();

  return (
    <MainWrapper>
      {!isCustomerLocation && !isAddEmergencyContact && (
        <TitleWrapper>{name}</TitleWrapper>
      )}
      {filteredFormsList &&
        filteredFormsList.length > 0 &&
        filteredFormsList.map((form, index) => {
          const repeatedFormTitle = `${formTitle} ${getSerialNumber(index)}`;
          let fieldsList = Object.keys(form);
          if (isStatementOfWork || isAppendices)
            fieldsList = getOriginalFieldSequence();
          return (
            <>
              <TitleWrapper>
                {repeatedFormTitle}
                {index !== 0 && renderDeleteButton(index)}
              </TitleWrapper>
              {fieldsList.map(formKey => {
                const fieldKey = `repeatedForm${key}${formKey}${index}`;
                const fieldFormKey = `${formKey}-${index}`;
                const currentValue = filteredFormsList[index][formKey];
                const fieldData = {
                  ...filteredFieldsMap[formKey],
                  name: fieldFormKey,
                  sysId: fieldKey,
                  value: currentValue,
                  onChange: ({ target: { value } }) =>
                    handleFormField(index, formKey, value)
                };
                let error =
                  repeatedFormError &&
                  !currentValue &&
                  filteredFieldsMap[formKey].required
                    ? fieldRequiredMessage
                    : '';
                if (error === '') {
                  if (repeatedFormError && currentValue) {
                    if (fieldData.fieldValidation) {
                      if (
                        fieldData.fieldValidation.fields.logic &&
                        fieldData.fieldValidation.fields.logic === 'regex'
                      ) {
                        const regex = new RegExp(
                          fieldData.fieldValidation.fields.value
                        );
                        if (!regex.test(currentValue)) {
                          error = fieldData.fieldValidation.fields.errorMessage;
                        }
                        if (currentValue.length > fieldData.maxLength) {
                          error = fieldData.fieldValidation.fields.errorMessage;
                        }
                      }
                    }
                  }
                }
                return (
                  <Fragment key={fieldKey}>
                    {fieldKey === fieldEmergencyPremiseContactNumber ||
                    fieldKey === fieldEmergencyExtension ? (
                      <InlineRepeatedFormField>
                        <FormField
                          key={`field${formKey}${fieldKey}`}
                          data={fieldData}
                        />
                        {error && <Error text={error} />}
                      </InlineRepeatedFormField>
                    ) : (
                      <div
                        style={
                          isCustomerLocation ? { paddingLeft: '1rem' } : {}
                        }
                      >
                        <FormField
                          key={`field${formKey}${fieldKey}`}
                          data={fieldData}
                        />
                        {error && <Error text={error} />}
                      </div>
                    )}
                  </Fragment>
                );
              })}
              {index === filteredFormsList.length - 1 && renderAddButton(index)}
            </>
          );
        })}
    </MainWrapper>
  );
};

RepeatedForm.propTypes = {
  data: PropTypes.shape({
    key: PropTypes.string.isRequired,
    formFields: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired,
    repeatBasedOnBackendField: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    formTitle: PropTypes.string
  }).isRequired,
  formErrors: PropTypes.objectOf(PropTypes.object),
  formFields: PropTypes.objectOf(PropTypes.object).isRequired,
  orderData: PropTypes.objectOf(PropTypes.object).isRequired,
  setFormFields: PropTypes.func.isRequired,
  isEditing: PropTypes.string.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  setIsFormTouched: PropTypes.func.isRequired
};

export default RepeatedForm;
